import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FaCopy, FaDownload, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';

// Sample gitignore templates (in a real app, you'd fetch from GitHub API)
const gitignoreTemplates = {
  'node': '# Logs\nlogs\n*.log\nnpm-debug.log*\n\n# Runtime data\npids\n*.pid\n*.seed\n\n# Directory for instrumented libs\ncoverage\n\n# Dependency directories\nnode_modules\n',
  'python': '# Byte-compiled / optimized / DLL files\n__pycache__/\n*.py[cod]\n\n# C extensions\n*.so\n\n# Distribution / packaging\n.Python\nenv/\nbuild/\n',
  'react': '# See https://help.github.com/articles/ignoring-files/ for more about ignoring files.\n\n# dependencies\n/node_modules\n/.pnp\n.pnp.js\n\n# testing\n/coverage\n',
  'java': '# Compiled class file\n*.class\n\n# Log file\n*.log\n\n# BlueJ files\n*.ctxt\n\n# Mobile Tools for Java (J2ME)\n.mtj.tmp/\n',
  'android': '# Built application files\n*.apk\n*.aar\n*.ap_\n*.aab\n\n# Files for the ART/Dalvik VM\n*.dex\n',
  'ios': '# Xcode\nbuild/\n*.pbxuser\n!default.pbxuser\n*.mode1v3\n!default.mode1v3\n',
  'windows': '# Windows thumbnail cache files\nThumbs.db\nThumbs.db:encryptable\n',
  'macos': '.DS_Store\n.AppleDouble\n.LSOverride\n',
  'linux': '*~\n\n# temporary files which can be created if a process still has a handle open.\n.fuse_hidden*\n',
  'visualstudio': '## Ignore Visual Studio temporary files, build results, and\n## files generated by popular Visual Studio add-ons.\n\n# User-specific files\n*.suo\n*.user\n*.userosscache\n',
  'intellij': '# User-specific stuff\n.idea/**\n*.iws\n*.iml\n*.ipr\n',
  'eclipse': '*.pydevproject\n.project\n.metadata\nbin/**\ntmp/**\n',
  'vim': '*.swp\n*.swo\n*.swn\n',
  'docker': '**/.dockerignore\n**/.env\n**/.docker\n',
  'terraform': '# Local .terraform directories\n**/.terraform/*\n\n# .tfstate files\n*.tfstate\n*.tfstate.*\n'
};

// const allTemplates = Object.keys(gitignoreTemplates);

const GitignoreGenerator = () => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [generatedContent, setGeneratedContent] = useState('');
  const [customRules, setCustomRules] = useState([]);
  const [newCustomRule, setNewCustomRule] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const { darkMode } = useTheme();

  // Fetch templates from GitHub API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://api.github.com/gitignore/templates');
        if (!response.ok) throw new Error('Failed to fetch templates');
        const templates = await response.json();
        setAvailableTemplates(templates);
        setError(null);
      } catch (err) {
        setError('Failed to load templates. Using local fallback.');
        // Fallback to local templates
        setAvailableTemplates(Object.keys(gitignoreTemplates));
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Search suggestions
  useEffect(() => {
    if (inputValue.length > 1) {
      const filtered = availableTemplates.filter(template =>
        template.toLowerCase().includes(inputValue.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [inputValue, availableTemplates]);

  // Fetch template content from GitHub
  const fetchTemplateContent = async (template) => {
    try {
      const response = await fetch(`https://api.github.com/gitignore/templates/${template}`);
      if (!response.ok) throw new Error('Failed to fetch template content');
      const data = await response.json();
      return data.source;
    } catch (err) {
      console.error(`Error fetching template ${template}:`, err);
      return gitignoreTemplates[template] || '# Template not available';
    }
  };

  const handleSelectTemplate = async (template) => {
    if (!selectedTemplates.includes(template)) {
      setSelectedTemplates([...selectedTemplates, template]);
      setInputValue('');
      setSuggestions([]);
    }
  };

  const removeTemplate = (templateToRemove) => {
    setSelectedTemplates(selectedTemplates.filter(t => t !== templateToRemove));
  };

  const addCustomRule = () => {
    if (newCustomRule.trim()) {
      setCustomRules([...customRules, newCustomRule.trim()]);
      setNewCustomRule('');
    }
  };

  const removeCustomRule = (index) => {
    setCustomRules(customRules.filter((_, i) => i !== index));
  };

  const generateGitignore = async () => {
    let content = '# Created using GitIgnore Generator\n\n';
    
    // Add selected templates
    for (const template of selectedTemplates) {
      content += `# ${template}\n${await fetchTemplateContent(template)}\n\n`;
    }

    // Add custom rules
    if (customRules.length > 0) {
      content += '# Custom Rules\n';
      customRules.forEach(rule => {
        content += `${rule}\n`;
      });
    }

    setGeneratedContent(content);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
  };

  const downloadGitignore = () => {
    const element = document.createElement('a');
    const file = new Blob([generatedContent], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = '.gitignore';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const resetGenerator = () => {
    setSelectedTemplates([]);
    setGeneratedContent('');
    setInputValue('');
    setCustomRules([]);
    setPreviewTemplate(null);
  };

  const previewTemplateContent = async (template) => {
    setPreviewTemplate({
      name: template,
      content: await fetchTemplateContent(template)
    });
  };

  return (
    <>
      <HeaderSection toolId="gitignoreGenerator" />
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <div className="bg-white dark:text-white rounded-xl dark:bg-gray-800 shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">
              .gitignore Generator
            </h1>
            <p className="opacity-90">
              Create perfect .gitignore files for your projects
            </p>
          </div>

          {/* Main Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md">
                {error}
              </div>
            )}

            {/* Tech/Language Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add technologies/languages
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 focus:ring-green-500 focus:border-green-500"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="e.g., Node, React, Python"
                />
                {suggestions.length > 0 && (
                  <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                    {suggestions.map((template) => (
                      <li
                        key={template}
                        className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex justify-between items-center"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <span>{template}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            previewTemplateContent(template);
                          }}
                          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          Preview
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Selected Templates */}
            <div className="mb-6">
              {selectedTemplates.length > 0 && (
                <>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Selected Templates:
                  </label>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedTemplates.map((template) => (
                      <div
                        key={template}
                        className="flex items-center bg-gray-200 dark:bg-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {template}
                        <button
                          onClick={() => removeTemplate(template)}
                          className="ml-2 text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Custom Rules */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Rules
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newCustomRule}
                  onChange={(e) => setNewCustomRule(e.target.value)}
                  className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  placeholder="e.g., *.log, /dist"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomRule()}
                />
                <button
                  onClick={addCustomRule}
                  className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md"
                >
                  <FaPlus />
                </button>
              </div>
              {customRules.length > 0 && (
                <div className="mt-2 space-y-2">
                  {customRules.map((rule, index) => (
                    <div key={index} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 p-2 rounded-md">
                      <span className="flex-1">{rule}</span>
                      <button
                        onClick={() => removeCustomRule(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Preview Modal */}
            {previewTemplate && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-medium">{previewTemplate.name}</h3>
                    <button
                      onClick={() => setPreviewTemplate(null)}
                      className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <FaTimes />
                    </button>
                  </div>
                  <div className="p-4 overflow-auto max-h-[60vh]">
                    <pre className="whitespace-pre-wrap text-sm">{previewTemplate.content}</pre>
                  </div>
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                    <button
                      onClick={() => setPreviewTemplate(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        handleSelectTemplate(previewTemplate.name);
                        setPreviewTemplate(null);
                      }}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md"
                    >
                      Add Template
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Generate Button */}
            <div className="mb-6">
              <button
                onClick={generateGitignore}
                disabled={selectedTemplates.length === 0 && customRules.length === 0}
                className={`px-4 py-2 rounded-md flex items-center gap-2 transition ${
                  selectedTemplates.length > 0 || customRules.length > 0
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 cursor-not-allowed'
                }`}
              >
                Create .gitignore
              </button>
            </div>

            {/* Generated Content */}
            {generatedContent && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Generated .gitignore
                </label>
                <div className="relative">
                  <pre className="w-full p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 overflow-x-auto max-h-96">
                    {generatedContent}
                  </pre>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      onClick={copyToClipboard}
                      className="p-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md"
                      title="Copy to clipboard"
                    >
                      <FaCopy />
                    </button>
                    <button
                      onClick={downloadGitignore}
                      className="p-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-md"
                      title="Download"
                    >
                      <FaDownload />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={resetGenerator}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-md flex items-center gap-2 transition"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-100 dark:text-white dark:bg-gray-800 p-4 text-center text-sm text-gray-600">
            <p>Create customized .gitignore files for your projects</p>
          </div>
        </div>
      </div>
      <BottomSection toolId="gitignoreGenerator" />
    </>
  );
};
export default GitignoreGenerator;   